/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';

import { useModalBackHandler } from 'src/hooks/use-modal-back-handler';

import { isValidLongPathSegment } from 'src/utils/route-param-id';

import learnApi from 'src/api/learnApi';

import Image from '../image';
import { useSettingsContext } from '../settings';
import { DarkModeWrapper } from '../layout/learn/view';

type Props = {
  nextLessonTitle: string;
  open: boolean;
  onClose: VoidFunction;
  moveNextLesson: VoidFunction;
  timer?: number;
  nextLesson: any;
  learnerId: string;
};

export default function MoveToNextLessonModal({
  nextLessonTitle,
  open,
  onClose,
  moveNextLesson,
  timer = 5,
  nextLesson,
  learnerId,
}: Readonly<Props>) {
  const { t } = useTranslation();
  const [count, setCount] = useState<number>(timer);
  const [nextLessonThumbnail, setNextLessonThumbnail] = useState<string | null>(null);
  const settings = useSettingsContext();

  useModalBackHandler(open, onClose);

  const handleMoveNextToLesson = () => {
    moveNextLesson();
    onClose();
  };

  const fetchNextLessonContent = async () => {
    if (
      !learnerId ||
      !nextLesson?.progressionId ||
      !isValidLongPathSegment(learnerId) ||
      !isValidLongPathSegment(nextLesson.progressionId)
    ) {
      return;
    }

    try {
      const response = await learnApi.learnLessonContents({
        learnerId,
        progressionId: nextLesson.progressionId,
      });

      const thumbnails = response?.data?.data?.video?.thumbnails;
      setNextLessonThumbnail(thumbnails ?? null);
    } catch (error) {
      console.error('Failed to fetch next lesson content:', error);
      setNextLessonThumbnail(null);
    }
  };

  useEffect(() => {
    fetchNextLessonContent();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (open) {
      if (count > 0) {
        intervalId = setInterval(() => {
          setCount((prevCount) => {
            if (prevCount <= 1) {
              clearInterval(intervalId);
              return 0;
            }
            return prevCount - 1;
          });
        }, 1000);
      }
    } else {
      return setCount(timer);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [open]);

  useEffect(() => {
    if (count === 0) {
      handleMoveNextToLesson();
    }
  }, [count]);

  return (
    <DarkModeWrapper themeMode={settings.themeMode}>
      <Dialog
        fullWidth
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              maxWidth: '520px',
              bgcolor: 'var(--background-default)',
            },
          },
        }}
      >
        <Stack padding="24px 24px 0">
          <Typography variant="h5" mb={2}>
            {t('learner.4239', { str: count })}
          </Typography>
          <Typography variant="body1" mb={0.5} color="var(--text-secondary)">
            {t('learner.959')}
          </Typography>
          <Typography variant="h5">{nextLessonTitle}</Typography>

          {nextLessonThumbnail && (
            <Stack sx={{ width: 1, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
              <Image
                ratio="4/3"
                alt={nextLessonTitle}
                src={
                  Array.isArray(nextLessonThumbnail) ? nextLessonThumbnail[0] : nextLessonThumbnail
                }
              />
            </Stack>
          )}
        </Stack>

        <DialogActions>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button color="primary" variant="outlined" onClick={onClose}>
              {t('learner.960')}
            </Button>
            <Button color="primary" variant="contained" onClick={() => handleMoveNextToLesson()}>
              {t('learner.4240')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </DarkModeWrapper>
  );
}
